import { BUILDING_DEFS, PATH_BUDGET_PER_TICK, colOf, inBounds, xOf, yOf } from '../constants';
import type { Simulation } from '../sim';
import type { Building, DigOrder, GoodKind, Peep, SimState } from '../types';
import { GOOD_KINDS } from '../types';
import { accessTiles, findPath } from '../world/path';
import { digWorkTiles } from '../world/terrain';

const scratchWorkTiles: number[] = [];
const MAX_ORDER_TRIES_PER_PEEP = 5;
/** How many nearest open orders a miner considers before pathing. */
const ORDER_CANDIDATES = 12;
/** A* expansion cap for cosmetic wandering — a ≤2-tile stroll never needs
 * more; unreachable targets must not flood-fill the whole map. */
const WANDER_MAX_NODES = 64;

function peepCol(peep: Peep): number {
  return colOf(Math.floor(peep.x), Math.floor(peep.y));
}

function manhattan(peep: Peep, order: DigOrder): number {
  return Math.abs(peep.x - (xOf(order.col) + 0.5)) + Math.abs(peep.y - (yOf(order.col) + 0.5));
}

/**
 * Runs every JOB_ASSIGN_INTERVAL_TICKS. Matches idle miners to the nearest
 * executable unclaimed dig order, idle haulers to the richest unclaimed pile,
 * and drifts the miner/hauler split toward jobTargets using idle peeps.
 */
export function assignJobs(sim: Simulation): void {
  const state = sim.state;
  const terrain = state.terrain;

  if (sim.jobsTerrainVersion !== terrain.version) {
    sim.jobsTerrainVersion = terrain.version;
    // Reachability only changes when the terrain does — un-suspend everything
    // and let the next pass re-prove what is still walled off.
    for (const order of state.orders.values()) order.suspended = false;
    for (const pile of state.goodsPiles.values()) pile.suspended = false;
    for (const building of state.buildings) building.outputSuspended = false;
  }

  rebalanceJobs(sim);

  // Collect and sort open orders ONCE per pass (shallowest first so fields
  // excavate in level waves; col breaks ties deterministically). Each miner
  // then picks the nearest of the first few unclaimed candidates — no more
  // per-peep re-sorting of the whole order book.
  let openOrders: DigOrder[] | null = null;
  // A* is the expensive step: budget the calls per pass so a 1000-peep town
  // spreads assignment over a few passes instead of spiking one tick.
  let pathCalls = 0;

  for (const peep of state.peeps) {
    if (peep.task.kind !== 'idle') continue;
    if (pathCalls >= PATH_BUDGET_PER_TICK) break;
    // Employment is job-driven, not role-driven: a settled spouse with a
    // mine job works the pit like anyone else. Only the unemployed
    // townsfolk (and kids) drift around for street life.
    if (peep.job !== 'miner' && peep.job !== 'hauler' && peep.role !== 'worker') {
      pathCalls += maybeWander(sim, peep);
      continue;
    }

    if (peep.job === 'miner') {
      if (openOrders === null) {
        openOrders = [];
        for (const order of state.orders.values()) {
          if (order.claimedBy === null && !order.suspended) openOrders.push(order);
        }
        openOrders.sort((a, b) => {
          const depthDiff = (terrain.depth[a.col] ?? 0) - (terrain.depth[b.col] ?? 0);
          return depthDiff !== 0 ? depthDiff : a.col - b.col;
        });
      }
      if (openOrders.length === 0) continue;
      let tries = 0;
      while (tries < MAX_ORDER_TRIES_PER_PEEP && pathCalls < PATH_BUDGET_PER_TICK) {
        // Nearest of the shallowest ORDER_CANDIDATES still-open orders.
        let order: DigOrder | null = null;
        let bestDistance = Infinity;
        let seen = 0;
        for (const candidate of openOrders) {
          if (candidate.claimedBy !== null || candidate.suspended) continue;
          const distance = manhattan(peep, candidate);
          if (distance < bestDistance) {
            bestDistance = distance;
            order = candidate;
          }
          seen += 1;
          if (seen >= ORDER_CANDIDATES) break;
        }
        if (order === null) break;
        tries += 1;
        const workTiles = digWorkTiles(terrain, order.col, scratchWorkTiles);
        if (workTiles.length === 0) {
          // No standable face within one level (e.g. a stranded pillar) —
          // gray it out until the terrain around it changes.
          order.suspended = true;
          continue;
        }
        pathCalls += 1;
        const path = findPath(terrain, peepCol(peep), new Set(workTiles));
        if (path === null) {
          order.suspended = true;
          continue;
        }
        order.claimedBy = peep.id;
        peep.task = { kind: 'toDig', col: order.col };
        peep.path = path.length > 0 ? path : null;
        peep.pathIdx = 0;
        peep.pathTerrainVersion = terrain.version;
        peep.repathFailures = 0;
        break;
      }
    } else if (peep.job === 'hauler') {
      if (peep.carryDt > 0) {
        // Interrupted mid-haul: route by what's actually on the back —
        // goods go to consumers/warehouse, never into the ore depot.
        pathCalls += 1;
        if (peep.carryGood !== null) sendGoodsCargo(sim, peep);
        else sendToDepot(sim, peep);
        continue;
      }
      // (1) Feed starving chain consumers before anything else — a stalled
      // ranch loses a whole day's output; the depot just waits.
      if (assignGoodsPickupForConsumers(sim, peep)) {
        pathCalls += 1;
        continue;
      }
      // (2) The richest unclaimed ore pile.
      let best: number = -1;
      let bestAmount = 0;
      for (const [col, pile] of state.piles) {
        if (pile.claimedDt > 0) continue;
        const amount = pile.ironDt + pile.goldDt;
        if (amount > bestAmount) {
          bestAmount = amount;
          best = col;
        }
      }
      if (best >= 0) {
        pathCalls += 1;
        const path = findPath(terrain, peepCol(peep), best);
        if (path === null) continue;
        const pile = state.piles.get(best)!;
        pile.claimedDt = 1;
        peep.task = { kind: 'toPile', col: best };
        peep.path = path.length > 0 ? path : null;
        peep.pathIdx = 0;
        peep.pathTerrainVersion = terrain.version;
        peep.repathFailures = 0;
        continue;
      }
      // (3) Surplus goods to the warehouse — post-ore this is the day job.
      // Ground piles first (they're overflow blocking land), then the
      // producers' own stores.
      if (goodsDepotSpace(state) > 0 && (assignGoodsPickup(sim, peep, null) || assignOutputPickup(sim, peep, null))) {
        pathCalls += 1;
      }
    }
  }
}

function goodsDepotSpace(state: SimState): number {
  let stored = 0;
  for (const good of GOOD_KINDS) stored += state.economy.goodsDepot.units[good];
  return state.economy.goodsDepot.capacityUnits - stored;
}

/** Chain consumers with room in the feed buffer, hungriest first. */
function starvingConsumers(state: SimState, good: GoodKind | null): Building[] {
  const result: Building[] = [];
  for (const building of state.buildings) {
    const consumes = BUILDING_DEFS[building.type].consumes;
    if (!consumes) continue;
    if (good !== null && consumes.good !== good) continue;
    if (building.inputUnits < consumes.bufferUnits) result.push(building);
  }
  return result.sort((a, b) => {
    const fillA = a.inputUnits / BUILDING_DEFS[a.type].consumes!.bufferUnits;
    const fillB = b.inputUnits / BUILDING_DEFS[b.type].consumes!.bufferUnits;
    return fillA !== fillB ? fillA - fillB : a.id - b.id;
  });
}

/** The fullest unclaimed pile of a good (or of any good when null). */
function richestGoodsPile(state: SimState, good: GoodKind | null): number {
  let best = -1;
  let bestUnits = 0;
  for (const [col, pile] of state.goodsPiles) {
    if (pile.claimedUnits > 0 || pile.suspended) continue;
    if (good !== null && pile.good !== good) continue;
    if (pile.units > bestUnits) {
      bestUnits = pile.units;
      best = col;
    }
  }
  return best;
}

/** Claim a goods pile and walk to it. `good` null = any good (warehouse run). */
function assignGoodsPickup(sim: Simulation, peep: Peep, good: GoodKind | null): boolean {
  const state = sim.state;
  const col = richestGoodsPile(state, good);
  if (col < 0) return false;
  const path = findPath(state.terrain, peepCol(peep), col);
  if (path === null) {
    // Walled in — stop re-failing it every pass until the terrain changes.
    state.goodsPiles.get(col)!.suspended = true;
    return false;
  }
  const pile = state.goodsPiles.get(col)!;
  pile.claimedUnits = 1;
  peep.task = { kind: 'toGoodsPile', col };
  peep.path = path.length > 0 ? path : null;
  peep.pathIdx = 0;
  peep.pathTerrainVersion = state.terrain.version;
  peep.repathFailures = 0;
  return true;
}

/** The fullest unclaimed, reachable output store of a good (any when null). */
function richestOutputBuilding(state: SimState, good: GoodKind | null): Building | null {
  let best: Building | null = null;
  for (const building of state.buildings) {
    const produces = BUILDING_DEFS[building.type].produces;
    if (!produces) continue;
    if (good !== null && produces.good !== good) continue;
    if (building.outputUnits <= 0 || building.outputClaimedUnits > 0 || building.outputSuspended) continue;
    // Fullest store wins; id breaks ties deterministically.
    if (best === null || building.outputUnits > best.outputUnits) best = building;
  }
  return best;
}

/** Claim a producer's output store and walk to the building. */
function assignOutputPickup(sim: Simulation, peep: Peep, good: GoodKind | null): boolean {
  const building = richestOutputBuilding(sim.state, good);
  if (building === null) return false;
  if (!routeToBuilding(sim, peep, building)) {
    building.outputSuspended = true;
    return false;
  }
  building.outputClaimedUnits = 1;
  peep.task = { kind: 'toOutputPickup', buildingId: building.id };
  return true;
}

/** Fetch feed for the hungriest consumer: ground piles of its good first
 * (they're in the open and block ground), then producers' output stores. */
function assignGoodsPickupForConsumers(sim: Simulation, peep: Peep): boolean {
  for (const consumer of starvingConsumers(sim.state, null)) {
    const good = BUILDING_DEFS[consumer.type].consumes!.good;
    if (assignGoodsPickup(sim, peep, good)) return true;
    if (assignOutputPickup(sim, peep, good)) return true;
  }
  return false;
}

function routeToBuilding(sim: Simulation, peep: Peep, building: Building): boolean {
  const state = sim.state;
  const def = BUILDING_DEFS[building.type];
  const targets = accessTiles(state.terrain, building.x, building.y, def.w, def.h);
  const path = findPath(state.terrain, peepCol(peep), targets);
  if (path === null) return false;
  peep.path = path.length > 0 ? path : null;
  peep.pathIdx = 0;
  peep.pathTerrainVersion = state.terrain.version;
  peep.repathFailures = 0;
  return true;
}

/** Route carried goods: hungriest consumer of that good first, then any
 * functional warehouse with depot space. False = nowhere to go (cargo stays
 * on the back; the next assign pass retries). */
export function sendGoodsCargo(sim: Simulation, peep: Peep): boolean {
  const state = sim.state;
  const good = peep.carryGood;
  if (good === null) return false;
  for (const consumer of starvingConsumers(state, good)) {
    if (routeToBuilding(sim, peep, consumer)) {
      peep.task = { kind: 'toGoodsDropoff', buildingId: consumer.id };
      return true;
    }
  }
  if (goodsDepotSpace(state) > 0) {
    for (const building of state.buildings) {
      if (BUILDING_DEFS[building.type].goodsCapacityUnits === undefined || !building.functional) continue;
      if (routeToBuilding(sim, peep, building)) {
        peep.task = { kind: 'toGoodsDropoff', buildingId: building.id };
        return true;
      }
    }
  }
  return false;
}

export function sendToDepot(sim: Simulation, peep: Peep): boolean {
  const state = sim.state;
  const depot = state.buildings.find((building) => building.type === 'oreDepot');
  if (!depot) return false;
  const space = state.economy.depot.capacityDt - state.economy.depot.ironDt - state.economy.depot.goldDt;
  if (space <= 0) return false;
  const def = BUILDING_DEFS.oreDepot;
  const targets = accessTiles(state.terrain, depot.x, depot.y, def.w, def.h);
  const path = findPath(state.terrain, peepCol(peep), targets);
  if (path === null) return false;
  peep.task = { kind: 'toDepot' };
  peep.path = path.length > 0 ? path : null;
  peep.pathIdx = 0;
  peep.pathTerrainVersion = state.terrain.version;
  peep.repathFailures = 0;
  return true;
}

/** Spouses off shift and kids drift around their block for street life.
 * Returns the number of A* calls spent (0 or 1) for the pass budget. */
function maybeWander(sim: Simulation, peep: Peep): number {
  if (peep.job === 'staff' || peep.path !== null) return 0;
  if (!sim.forks.families.chance(0.03)) return 0;
  const terrain = sim.state.terrain;
  const tx = Math.floor(peep.x) + sim.forks.families.integer(-2, 2);
  const ty = Math.floor(peep.y) + sim.forks.families.integer(-2, 2);
  if (!inBounds(tx, ty)) return 0;
  const path = findPath(terrain, colOf(Math.floor(peep.x), Math.floor(peep.y)), colOf(tx, ty), WANDER_MAX_NODES);
  if (path && path.length > 0 && path.length <= 6) {
    peep.path = path;
    peep.pathIdx = 0;
    peep.pathTerrainVersion = terrain.version;
  }
  return 1;
}

function rebalanceJobs(sim: Simulation): void {
  const state = sim.state;
  // Any adult can work the mine — original hands and settled townsfolk alike.
  // Staff and industry hands stay at their posts; the rebalance never poaches.
  const workers = state.peeps.filter(
    (peep) =>
      peep.role !== 'child' && peep.job !== 'staff' && peep.job !== 'industry' && peep.task.kind !== 'offDuty',
  );
  let miners = workers.filter((peep) => peep.job === 'miner').length;
  let haulers = workers.filter((peep) => peep.job === 'hauler').length;
  for (const peep of workers) {
    if (peep.task.kind !== 'idle' || peep.carryDt > 0) continue;
    if (miners < state.jobTargets.miners && peep.job !== 'miner') {
      if (peep.job === 'hauler' && haulers <= state.jobTargets.haulers) continue;
      if (peep.job === 'hauler') haulers -= 1;
      peep.job = 'miner';
      miners += 1;
    } else if (haulers < state.jobTargets.haulers && peep.job !== 'hauler') {
      if (peep.job === 'miner' && miners <= state.jobTargets.miners) continue;
      if (peep.job === 'miner') miners -= 1;
      peep.job = 'hauler';
      haulers += 1;
    } else if (peep.job === 'miner' && miners > state.jobTargets.miners) {
      peep.job = haulers < state.jobTargets.haulers ? 'hauler' : 'idle';
      miners -= 1;
      if (peep.job === 'hauler') haulers += 1;
    } else if (peep.job === 'hauler' && haulers > state.jobTargets.haulers) {
      peep.job = miners < state.jobTargets.miners ? 'miner' : 'idle';
      haulers -= 1;
      if (peep.job === 'miner') miners += 1;
    }
  }
}
